import {Dropdown, message, Modal, Upload} from "antd";
import {SearchableUpload} from "./EditScreenContentModal";
import {useRecoilState} from "recoil";
import {useContext, useState} from "react";
import {memberState} from "../../state/atoms";
import {AppContext} from "../../AppContext";
import QRDisplayUploadsApi from "../../api/QRDisplayUploadsApi";
import {Button} from "../account/misc/Button";
import './UploadDisplayImage.scss';
import MyUploads, {Tab} from "./MyUploads";
import { PlusOutlined } from "@ant-design/icons";
import DSAApi from "../../api/DSAApi";
import { DsaUploadFolderDto } from "../../generated/dashboard-client";

export const AcceptedDisplayFileTypes = ["image/jpeg", "image/png", "image/gif", "video/mp4"];
export const displayFileTypeErrorMessage = "Supported file types are .png, .jpg/.jpeg, .gif, and .mp4.";
export const maxDisplayFileSize = 10;
export const displayFileSizeErrorMessage = `This file can't be uploaded because it exceeds the ${maxDisplayFileSize} MB file size limit.`;

interface UploadDisplayImageProps {
  className?: string;
  beforeUpload: () => void;
  afterUpload: () => void;
  uploads: SearchableUpload[];
  setUploads: (uploads: SearchableUpload[]) => void;
  folderId?: number;
  folders?: DsaUploadFolderDto[];
  ensureFolderCounts?: (currentUploads: SearchableUpload[], currentFolders: DsaUploadFolderDto[]) => void;
}

export default function UploadDisplayImage({beforeUpload, afterUpload, uploads, setUploads, className = '', folderId, folders, ensureFolderCounts}: UploadDisplayImageProps) {
  const [member] = useRecoilState(memberState);
  const {location} = useContext(AppContext);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [preselectedTab, setPreselectedTab] = useState(Tab.SelectFromUploads);

  function onUploadChange(file: File) {
    if (AcceptedDisplayFileTypes.indexOf(file.type?.toLowerCase() ?? "") === -1) {
      message.error(displayFileTypeErrorMessage);
      return;
    }

    if ((file.size ?? 0) > maxDisplayFileSize * 1024 * 1024) {
      message.error(displayFileSizeErrorMessage);
      return;
    }

    const newFile = new File([file], file.name.replace(/ /g, "_"), {
      type: file.type,
      lastModified: file.lastModified
    });

    const formData = new FormData();
    formData.append("file", newFile);

    beforeUpload();

    QRDisplayUploadsApi.uploadDisplayAppImage(member.internalId.toString(), location.internalId!.toString(), formData).then((resp) => {
      const newUpload = resp.data;
      if (folderId && folders && ensureFolderCounts) {
        newUpload.folderId = folderId.toString();
        DSAApi.putUploadsInFolder([newUpload.id], folderId).then(() => {
          ensureFolderCounts([{...newUpload, visible: true, selected: false} as SearchableUpload, ...uploads], folders);
        })
        .catch(() => {
          console.log("Error moving new upload to folder");
        });
      }
      setUploads([{...newUpload, visible: true, selected: false} as SearchableUpload, ...uploads]);
      afterUpload();
    }).catch(() => {
      console.error("Error uploading display image");
    });
  }

  function chooseFromMyUploads() {
    setPreselectedTab(Tab.SelectFromUploads);
    setModalOpen(true);
  }

  function chooseFromPOSUploads() {
    setPreselectedTab(Tab.SelectFromPos);
    setModalOpen(true);
  }

  return <>
    <Dropdown placement="bottomLeft"
              open={dropdownOpen}
              onOpenChange={(o) => setDropdownOpen(o)}
              trigger={['click']}
              overlayClassName="upload-dropdown"
              getPopupContainer={(trigger) => trigger.parentNode as HTMLElement}
              overlay={<ul className="upload-dropdown-ul" onClick={() => setDropdownOpen(false)}>
                <Upload accept={AcceptedDisplayFileTypes.join(",")} showUploadList={false} beforeUpload={onUploadChange}
                        className={className}>
                  <li>Upload Files</li>
                </Upload>
                <li onClick={chooseFromMyUploads}>Choose from My Uploads</li>
                <li onClick={chooseFromPOSUploads}>Choose from POS Images</li>
              </ul>}>
      <Button className="upload-btn">
        <><PlusOutlined/><span>Upload</span></>
      </Button>
    </Dropdown>
    {modalOpen && <Modal className={"my-uploads-modal-wrapper"} open={true} onCancel={() => setModalOpen(false)}>
      <MyUploads onClose={() => setModalOpen(false)} onSelect={onUploadChange} preselectedTab={preselectedTab}/>
    </Modal>}
  </>
}
